import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TabLocationContext } from '../auth/auth.interface';

const TAB_CONTEXT_KEY = 'tab_location_context';
const DEFAULT_HINT_KEY = 'tab_location_default_hint';

@Injectable({
  providedIn: 'root'
})
export class LocationContextService {
  private currentLocationIdSubject = new BehaviorSubject<number | null>(null);
  public currentLocationId$ = this.currentLocationIdSubject.asObservable();
  private adminViewModeSubject = new BehaviorSubject<boolean>(false);
  public isAdminViewMode$ = this.adminViewModeSubject.asObservable();
  private rolesSubject = new BehaviorSubject<string[]>([]);
  public roles$ = this.rolesSubject.asObservable();
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();

  constructor() {
    let ctx = this.readTabContext();

    // New tab (empty sessionStorage): bootstrap from the shared hint in localStorage.
    // This gives new tabs the same location context as the last active tab.
    if (!ctx) {
      ctx = this.readDefaultHint();
      if (ctx) {
        // Persist the hint into this tab's sessionStorage so subsequent reads
        // and hasTabContext() work correctly.
        sessionStorage.setItem(TAB_CONTEXT_KEY, JSON.stringify(ctx));
      }
    }

    if (ctx) {
      this.adminViewModeSubject.next(ctx.is_admin_view_mode);
      if (ctx.is_admin_view_mode) {
        this.currentLocationIdSubject.next(null);
      } else {
        this.currentLocationIdSubject.next(ctx.current_location_id);
      }
      this.rolesSubject.next(ctx.roles ?? []);
      this.permissionsSubject.next(ctx.permissions ?? []);
    }
  }

  /**
   * Get current location ID
   */
  getCurrentLocationId(): number | null {
    return this.currentLocationIdSubject.value;
  }

  /**
   * Set current location ID
   */
  setCurrentLocationId(locationId: number): void {
    this.currentLocationIdSubject.next(locationId);
    this.adminViewModeSubject.next(false);
    this.writeTabContext();
  }

  /**
   * Clear current location ID
   */
  clearCurrentLocationId(): void {
    this.currentLocationIdSubject.next(null);
    this.writeTabContext();
  }

  /**
   * Check whether Admin View mode is enabled
   */
  isAdminViewMode(): boolean {
    return this.adminViewModeSubject.value;
  }

  /**
   * Set Admin View mode
   */
  setAdminViewMode(enabled: boolean): void {
    this.adminViewModeSubject.next(enabled);
    if (enabled) {
      this.currentLocationIdSubject.next(null);
    }
    this.writeTabContext();
  }

  /**
   * Clear Admin View mode
   */
  clearAdminViewMode(): void {
    this.adminViewModeSubject.next(false);
    this.writeTabContext();
  }

  /**
   * Check if location context is set
   */
  hasLocationContext(): boolean {
    return this.currentLocationIdSubject.value !== null;
  }

  /**
   * Set location-scoped roles and permissions for this tab
   */
  setRolesAndPermissions(roles: string[], permissions: string[]): void {
    this.rolesSubject.next(roles);
    this.permissionsSubject.next(permissions);
    this.writeTabContext();
  }

  /**
   * Get current tab's roles
   */
  getRoles(): string[] {
    return this.rolesSubject.value;
  }

  /**
   * Get current tab's permissions
   */
  getPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  /**
   * Check if this tab has an initialized location context in sessionStorage
   */
  hasTabContext(): boolean {
    return sessionStorage.getItem(TAB_CONTEXT_KEY) !== null;
  }

  /**
   * Clear all tab-scoped location context (used on logout)
   */
  clearAll(): void {
    this.currentLocationIdSubject.next(null);
    this.adminViewModeSubject.next(false);
    this.rolesSubject.next([]);
    this.permissionsSubject.next([]);
    sessionStorage.removeItem(TAB_CONTEXT_KEY);
    localStorage.removeItem(DEFAULT_HINT_KEY);
  }

  private readTabContext(): TabLocationContext | null {
    const raw = sessionStorage.getItem(TAB_CONTEXT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as TabLocationContext;
    } catch {
      return null;
    }
  }

  private readDefaultHint(): TabLocationContext | null {
    const raw = localStorage.getItem(DEFAULT_HINT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as TabLocationContext;
    } catch {
      return null;
    }
  }

  private writeTabContext(): void {
    const ctx: TabLocationContext = {
      current_location_id: this.currentLocationIdSubject.value,
      is_admin_view_mode: this.adminViewModeSubject.value,
      roles: this.rolesSubject.value,
      permissions: this.permissionsSubject.value,
    };
    sessionStorage.setItem(TAB_CONTEXT_KEY, JSON.stringify(ctx));
    // Update the shared hint so new tabs inherit the latest location context.
    localStorage.setItem(DEFAULT_HINT_KEY, JSON.stringify(ctx));
  }
}
